import { useMutation } from '@apollo/client';
import { useCurrentAccount } from '@application/api';
import { graphql } from '@gql/gql';
import type {
  MtLitterGetLitterQuery,
  MtLitterUpdateLitterMutation,
  MtLitterUpdateLitterMutationVariables,
} from '@gql/graphql';
import type { MtLitterPageFormValues } from '@presentation/pages';
import cleanDeep from 'clean-deep';
import { useCallback } from 'react';
import { MT_LITTERS_GET_LITTERS } from '../../MtLittersPageController/api/useGetLitters';
import { useLitterUpdateBuilder } from './useLitterUpdateBuilder';

export const MT_LITTER_UPDATE_LITTER = graphql(`
  mutation MtLitterUpdateLitter(
    $data: UpdateLitterInput!
    $litterId: Int!
    $imageUpload: [Upload!]
    $imageIdsRemoved: [Int!]
  ) {
    updateLitter(
      data: $data
      litterId: $litterId
      imagesUpload: $imageUpload
      imageIdsRemoved: $imageIdsRemoved
    ) {
      id
    }
  }
`);

export const useUpdateLitter = (
  litter?:
    | NonNullable<NonNullable<MtLitterGetLitterQuery['accountMe']>['dogBreeder']>['litters'][number]
    | null,
) => {
  const [updateLitter, { error, loading }] = useMutation<
    MtLitterUpdateLitterMutation,
    MtLitterUpdateLitterMutationVariables
  >(MT_LITTER_UPDATE_LITTER, {
    refetchQueries: [MT_LITTERS_GET_LITTERS],
  });

  const { currentAccountLoading, currentAccountError } = useCurrentAccount();
  const { litterUpdateBuilder } = useLitterUpdateBuilder();

  const updateLitterFromForm = useCallback(
    (form: MtLitterPageFormValues) => {
      if (litter == null) return;

      const { data } = litterUpdateBuilder(form, litter);

      const formImageUrls: string[] = cleanDeep(form.images ?? [])
        ?.map((file) => (typeof file === 'string' ? file : undefined))
        .filter((file) => file != null) as string[];

      const deleteImageIds = (litter.imgs ?? [])
        .filter((img) => !formImageUrls.includes(img.small ?? ''))
        .map((img) => img.id);

      const imageUpload: Scalar_Upload[] = cleanDeep(form.images ?? [])
        ?.map((file) => (typeof file === 'string' ? undefined : (file as Scalar_Upload)))
        .filter((file) => file != null) as Scalar_Upload[];

      return updateLitter({
        variables: {
          litterId: litter.id,
          data,
          imageIdsRemoved: deleteImageIds,
          ...(imageUpload?.length ? { imageUpload } : {}),
        },
      });
    },
    [litter, litterUpdateBuilder, updateLitter],
  );

  return {
    updateLitterLoading: loading || currentAccountLoading,
    updateLitterError: error || currentAccountError,
    updateLitterFromForm,
    updateLitter,
  };
};
